import cloneDeep from "lodash/cloneDeep";
import type { DiscountOffer, Sale } from "../types";
import type { PartnerDefinition } from "./types";

/**
 * A partner's conversation steps: what to SEND, and what each answer MEANS (DISCOUNT_V2_PLAN.md
 * §9.12, decision 25; R2.4c, 2026-10-12). Moved from `discounts/external.ts`, behaviour unchanged.
 *
 * 🔴 **A partner = the default steps + its data + only the steps it overrides.** Every default
 * below reads the partner's own data (its `PartnerDefinition` fields), so most differences between
 * partners are data; a partner whose ANSWER or PAYLOAD differs in one part overrides that one step
 * in its folder (`definePartner(metaId, { …, steps: (partner) => ({ readVerifyAnswer }) })`) and
 * inherits the rest. Shared code calls `getPartner(metaId).steps.<step>` and never names a partner.
 *
 * Pure: request builders take arguments and return bodies; readers take an answer and return a
 * named outcome. Nothing here sends: the partner port (`port.ts`) does, over the declared routes (R2.4c.2).
 *
 * ## The conversation
 *
 * ```
 *   apply  ──▶ readApplyAnswer(answer, offer)
 *                ├── "apply-now"  ──▶ write the discount onto plan.sale
 *                ├── "needs-otp"  ──▶ open the dialog, then:
 *                │      verify ──▶ readVerifyAnswer(answer)
 *                │                   ├── "verified" ──▶ write onto outcome.sale
 *                │                   └── "failed"   ──▶ report outcome.message
 *                └── "failed"     ──▶ report plan.code
 * ```
 *
 * The two functions this replaced in the old component, `sendApplyRequest` and
 * `externalDiscountOtpValidate`, carried nine `isExternalProviderC1/C2/C1C2` calls between them,
 * and because those predicates overlap (`C1C2 ⊃ C1`) no single site told you which partners reached
 * it.
 */

/* ── the outcomes ──────────────────────────────────────────────────────────────────────────── */

/**
 * What to do with a partner's APPLY answer.
 *
 * Three genuinely different next actions, hence a union rather than booleans on the answer: a
 * caller that mixes them up either opens an OTP dialog over an applied discount, or writes a
 * discount the partner has not confirmed.
 */
export type ApplyPlan =
  /** Open the OTP dialog; the discount is NOT on the bill yet. */
  | { kind: "needs-otp"; sale?: Sale; referenceId?: string }
  /** The partner is done: write the discount onto the sale it returned. */
  | { kind: "apply-now"; sale?: Sale; referenceId?: string }
  /** The partner refused. `code` is a backend error code where it gave one. */
  | { kind: "failed"; code?: string };

/**
 * What a partner's VERIFY answer means. `sale` is the priced sale the partner returns and the ONLY
 * sale the discount may be written onto.
 */
export type VerifyOutcome =
  | {
      kind: "verified";
      sale?: Sale;
      referenceId?: string;
      /** `redemptionInfo.data.blockedUntil`: how long the partner has reserved the coupon. */
      blockedUntil?: string;
      /**
       * The partner's handle for the reservation `blockedUntil` dates.
       *
       * ⚠️ Read from the answer's TOP LEVEL, not from `redemptionInfo.data` where its companion
       * `blockedUntil` lives: see `readVerifyAnswer`.
       */
      blockId?: string;
      /** Per-item discounts to stamp onto the sale, for a partner that returns them. */
      itemDiscounts: any[];
    }
  | { kind: "failed"; message?: string; code?: string };

/* ── the requests ──────────────────────────────────────────────────────────────────────────── */

/** The `redeemPoints` body that asks a partner to apply one of its offers. */
export interface ApplyRequest {
  loyaltyInput: { discountCode?: string; couponCode?: string };
  providerId?: string;
  metaId?: string;
  sale: Sale;
}

/** A verify body, in whichever of the two shapes the partner speaks. */
export type VerifyRequest =
  | {
      loyaltyInput: { discountCode?: string; couponCode?: string; referenceId?: string; otp: string };
      providerId?: string;
      metaId?: string;
      sale: Sale;
    }
  | {
      discountCode?: string;
      couponCode?: string;
      otp: string;
      providerId?: string;
      metaId?: string;
      sale: Sale;
    };

/** Everything the release (unblock) call needs, and whether it has it. */
export interface UnblockRequest {
  referenceId?: string;
  providerId?: string;
  metaId?: string;
  couponCode?: string;
  customerPhone?: string;
  /**
   * The partner's correlation id the entry kept (`requestId`, EasyRewardz): the backend's release
   * hands it to the partner (`saletxn_controller.js` 2271-2280), which unblocks by it. v1 never sent
   * it (decision 40, 2026-10-12).
   */
  requestId?: string;
  /**
   * The bill the coupon comes off, sent whole (web `removeExternalDiscount`, `326a931a4`): the
   * partner prices the release against it. Not part of any partner's completeness check.
   */
  sale?: unknown;
}

/** What the request builders need beside the offer. */
export interface RequestContext {
  sale: Sale;
  providerId?: string;
  metaId?: string;
  /** `discountStore.discountInfo`: the coupon check's last answer. */
  appliedDiscountInfo?: Record<string, any>;
}

/* ── the steps ─────────────────────────────────────────────────────────────────────────────── */

export interface PartnerSteps {
  /**
   * The apply request for a partner offer.
   *
   * ⚠️ `couponCode` prefers the one the COUPON API attributed to a typed code over the offer's
   * own. A cashier who types a partner code gets an `appliedDiscountInfo.couponCode` that is the
   * customer-specific instance of a campaign; the offer record carries the campaign's.
   */
  buildApplyRequest(offer: DiscountOffer, context: RequestContext): ApplyRequest;

  /**
   * What to do with the apply answer. The partner decides three things:
   *
   * | question | field | was |
   * |---|---|---|
   * | is there a second leg at all? | `requiresOtpOnApply` | `isExternalProviderC1C2(metaId)` |
   * | may this offer waive it? | `honoursOfferOtpFlag` | `isExternalProviderC1(...) && !data.isOtpRequired` |
   * | is a `referenceId` on this leg? | `referenceIdSource === "apply"` | `isExternalProviderC1(response.provider)` |
   *
   * ⚠️ The `referenceId` is read only when the partner says this leg carries one: reading it
   * unconditionally stored `undefined` over a good id for a partner that issues it on VERIFY.
   * ⚠️ An answer with neither a `redemptionInfo` nor a `sale` is a FAILURE, not a silent success.
   */
  readApplyAnswer(answer: any, offer: DiscountOffer | null | undefined): ApplyPlan;

  /**
   * The verify request and the route it goes to.
   *
   * 🔴 **The shape and the channel are decided together.** A nested body sent to the offer
   * endpoint, or a flat one sent to the loyalty endpoint, is rejected as malformed; the old code
   * kept the two facts in one ternary, so a partner that needed one and not the other (Blue Tokai)
   * could not be expressed. ⚠️ The `referenceId` rides along ONLY in the nested shape; the flat
   * shape's endpoint correlates by coupon code.
   */
  buildVerifyRequest(
    offer: DiscountOffer,
    otp: string,
    context: RequestContext & { referenceId?: string },
  ): { channel: "loyalty-redeem" | "offer-verify"; body: VerifyRequest };

  /**
   * What a verify answer means. The success signal is per partner (`redemptionInfo.success` on the
   * loyalty-shaped channel, `message === "success"` on the offer-shaped one), read once here.
   *
   * ⚠️ `itemDiscounts` is `[]` for a partner that returns none. ⚠️ A `code` is surfaced apart from
   * `message`: codes map to POS messages, `message` is the partner's prose. ⚠️ The coupon
   * RESERVATION is two fields at two depths: `blockedUntil` under `redemptionInfo.data`, `blockId`
   * at the top level. Both go onto the bill entry together.
   */
  readVerifyAnswer(answer: any): VerifyOutcome;

  /**
   * Whether this partner's coupon can be released, and with what. `{ ok: false, missing }`: the
   * partner requires a field we do not have, so the call is skipped and the caller reports
   * `missing`; sending it anyway produces a rejection the cashier cannot act on.
   */
  planRelease(
    request: UnblockRequest,
  ): { ok: true; body: UnblockRequest } | { ok: false; missing: "customerPhone" | "other" };

  /**
   * A CLONE of the partner's sale with the customer marked as verified BY THE PARTNER
   * (`extAuthenticated`, `extAuthenticatedBy` copied from `authenticated`, `authenticatedBy`).
   *
   * 🔴 Skipped for a partner whose OTP verifies the OFFER rather than the customer
   * (`stampsExternalAuthentication`): setting it there satisfies a customer-verification gate
   * nobody passed. Returns the sale untouched then, so the caller needs no branch.
   */
  stampAuthentication(sale: Sale): Sale;

  /** Are there per-item discounts to stamp onto the sale, and does this partner send them? */
  shouldApplyItemDiscounts(itemDiscounts: any[] | undefined): boolean;
}

/** The default steps, reading `partner`'s data. A partner's folder overrides only what differs. */
export const defaultSteps = (partner: PartnerDefinition): PartnerSteps => ({
  buildApplyRequest: (offer, { sale, providerId, metaId, appliedDiscountInfo = {} }) => ({
    loyaltyInput: {
      discountCode: offer?.discountCode,
      couponCode: appliedDiscountInfo?.couponCode || offer?.couponCode,
    },
    providerId,
    metaId,
    sale,
  }),

  readApplyAnswer: (answer, offer) => {
    const code = answer?.code || answer?.errorCode;
    if (code) return { kind: "failed", code };

    const redemptionInfo = answer?.redemptionInfo;
    const referenceId = partner.referenceIdSource === "apply" ? redemptionInfo?.data?.referenceId : undefined;

    // The partner answered a challenge, unless this offer waives it and the partner lets an offer do that.
    if (redemptionInfo && partner.requiresOtpOnApply) {
      const waived = partner.honoursOfferOtpFlag && redemptionInfo?.success && offer?.isOtpRequired === false;
      return waived
        ? { kind: "apply-now", sale: answer?.sale, referenceId }
        : { kind: "needs-otp", sale: answer?.sale, referenceId };
    }

    if (answer?.sale) return { kind: "apply-now", sale: answer.sale, referenceId };
    return { kind: "failed" };
  },

  buildVerifyRequest: (offer, otp, { sale, providerId, metaId, referenceId, appliedDiscountInfo = {} }) => {
    const couponCode = offer?.couponCode || appliedDiscountInfo?.couponCode;
    const body: VerifyRequest = partner.wrapsVerifyInLoyaltyInput
      ? { loyaltyInput: { discountCode: offer?.discountCode, couponCode, referenceId, otp }, providerId, metaId, sale }
      : { discountCode: offer?.discountCode, couponCode, otp, providerId, metaId, sale };
    return { channel: partner.verifyChannel, body };
  },

  readVerifyAnswer: (answer) => {
    const redemptionInfo = answer?.redemptionInfo;
    const succeeded =
      partner.verifySuccessSignal === "redemption-info" ? !!redemptionInfo?.success : answer?.message === "success";
    if (!succeeded) return { kind: "failed", message: answer?.message, code: answer?.code };

    return {
      kind: "verified",
      sale: answer?.sale,
      referenceId: partner.referenceIdSource === "verify" ? redemptionInfo?.data?.referenceId : undefined,
      blockedUntil: redemptionInfo?.data?.blockedUntil || undefined,
      // 🔴 `answer.blockId`, NOT `redemptionInfo.data.blockId`: the two reservation fields arrive
      // at DIFFERENT depths, so reading this one where its companion lives yields `undefined`.
      blockId: answer?.blockId || undefined,
      itemDiscounts: partner.returnsItemLevelDiscounts ? (redemptionInfo?.discounts ?? []) : [],
    };
  },

  planRelease: (request) => {
    if (partner.unblockRequiresReferenceAndPhone) {
      if (!request.customerPhone) return { ok: false, missing: "customerPhone" };
      if (!request.referenceId || !request.providerId || !request.couponCode) return { ok: false, missing: "other" };
    }
    return { ok: true, body: request };
  },

  stampAuthentication: (sale) => {
    if (!partner.stampsExternalAuthentication) return sale;
    // `cloneDeep`, not `structuredClone`: Hermes has none.
    const next: any = cloneDeep(sale);
    if (!next?.customerInfo) return next;
    next.customerInfo.extAuthenticated = next.customerInfo.authenticated;
    next.customerInfo.extAuthenticatedBy = next.customerInfo.authenticatedBy;
    return next;
  },

  shouldApplyItemDiscounts: (itemDiscounts) => !!itemDiscounts?.length && partner.returnsItemLevelDiscounts,
});

/**
 * The `referenceId` to STORE after a leg, or `undefined` to leave the stored one alone. Not per
 * partner: both readers already leave the id out on the leg the partner says does not carry it.
 */
export const getReferenceIdToStore = (outcome: { referenceId?: string } | null | undefined): string | undefined =>
  outcome?.referenceId || undefined;
